import {
  createElement,
  useLayoutEffect,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import type { DatabaseSortClause } from '@/lib/databases/build-query';

import { IconSortAsc, IconSortDesc } from './DatabaseMenuIcons.web';

const SUBMENU_MIN_WIDTH = 200;

function anchorSubmenuRight(rowEl: HTMLElement): { left: number; top: number } {
  const rect = rowEl.getBoundingClientRect();
  return { left: rect.right + 4, top: rect.top };
}

function CheckIcon({ visible }: { visible: boolean }) {
  if (!visible) {
    return createElement('span', { className: 'cs-db-calc-check' });
  }
  return createElement(
    'span',
    { className: 'cs-db-calc-check cs-db-calc-check--on', 'aria-hidden': 'true' },
    '✓',
  );
}

export function columnSortDirection(
  sorts: DatabaseSortClause[],
  columnId: string,
): 'asc' | 'desc' | null {
  for (const clause of sorts) {
    if (typeof clause.field === 'object' && clause.field.propertyId === columnId) {
      return clause.direction;
    }
  }
  return null;
}

/** Apply sort on column as primary; keep other column sorts after it. */
export function applyColumnSort(
  sorts: DatabaseSortClause[],
  columnId: string,
  direction: 'asc' | 'desc',
): DatabaseSortClause[] {
  const rest = sorts.filter(
    (clause) =>
      !(typeof clause.field === 'object' && clause.field.propertyId === columnId),
  );
  return [{ field: { propertyId: columnId }, direction }, ...rest];
}

function SubmenuItem({
  label,
  icon,
  selected,
  onPress,
}: {
  label: string;
  icon: ReturnType<typeof IconSortAsc>;
  selected: boolean;
  onPress: () => void;
}) {
  return createElement(
    'button',
    {
      type: 'button',
      className: 'cs-db-calc-item',
      onClick: (e: MouseEvent) => {
        e.stopPropagation();
        onPress();
      },
    },
    createElement('span', { className: 'cs-menu-item-icon' }, icon),
    createElement('span', { className: 'cs-db-calc-item-label' }, label),
    createElement(CheckIcon, { visible: selected }),
  );
}

export function ColumnHeaderSubmenuRow({
  label,
  icon,
  active,
  buttonRef,
  onHover,
}: {
  label: string;
  icon?: ReturnType<typeof IconSortAsc>;
  active?: boolean;
  buttonRef?: (el: HTMLButtonElement | null) => void;
  onHover?: () => void;
}) {
  return createElement(
    'button',
    {
      ref: buttonRef,
      type: 'button',
      className: `cs-db-calc-item${active ? ' cs-db-calc-item--active' : ''}`,
      onMouseEnter: onHover,
      onClick: (e: MouseEvent) => e.stopPropagation(),
    },
    icon ? createElement('span', { className: 'cs-menu-item-icon' }, icon) : null,
    createElement('span', { className: 'cs-db-calc-item-label' }, label),
    createElement('span', { className: 'cs-db-calc-chevron', 'aria-hidden': 'true' }, '›'),
  );
}

export function ColumnSortSubmenu({
  anchorRow,
  columnId,
  sorts,
  onSelect,
  onPointerEnter,
}: {
  anchorRow: HTMLButtonElement | null;
  columnId: string;
  sorts: DatabaseSortClause[];
  onSelect: (direction: 'asc' | 'desc') => void;
  onPointerEnter?: () => void;
}) {
  const [position, setPosition] = useState({ left: 0, top: 0 });
  const current = columnSortDirection(sorts, columnId);

  useLayoutEffect(() => {
    if (!anchorRow) {
      return;
    }
    setPosition(anchorSubmenuRight(anchorRow));
  }, [anchorRow]);

  if (!anchorRow || typeof document === 'undefined') {
    return null;
  }

  const maxHeight =
    typeof window !== 'undefined'
      ? Math.max(120, window.innerHeight - position.top - 8)
      : undefined;

  return createPortal(
    createElement(
      'div',
      {
        'data-cs-column-menu': 'true',
        className:
          'fixed overflow-y-auto overflow-x-hidden rounded-lg border border-border bg-surface py-1 shadow-xl',
        style: {
          left: position.left,
          top: position.top,
          minWidth: SUBMENU_MIN_WIDTH,
          maxHeight,
          zIndex: 100000,
        },
        onMouseDown: (e: MouseEvent) => e.stopPropagation(),
        onMouseEnter: onPointerEnter,
      },
      createElement(SubmenuItem, {
        label: 'Sort ascending',
        icon: IconSortAsc(),
        selected: current === 'asc',
        onPress: () => onSelect('asc'),
      }),
      createElement(SubmenuItem, {
        label: 'Sort descending',
        icon: IconSortDesc(),
        selected: current === 'desc',
        onPress: () => onSelect('desc'),
      }),
    ),
    document.body,
  );
}
